'use client';

import { useState } from 'react';

import { IProduct } from '@/services/types';

import ProductAddToCartButton from './ProductAddToCartButton';
import ProductQtySelector from './ProductQtySelector';
import ProductVariantSelector, { type ProductVariant } from './ProductVariantSelector';

type ProductSelectorsProps = {
  product: IProduct;
};

const ProductSelectors: React.FC<ProductSelectorsProps> = (props) => {
  const { product } = props;
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [quantity, setQuantity] = useState<number>(1);

  const handleVariantChange = (variant: ProductVariant | null) => {
    setSelectedVariant(variant);
  };

  const handleQuantityChange = (qty: number) => {
    setQuantity(qty);
  };

  const hasVariants = !!(product.variants?.length || product.colors?.length || product.sizes?.length);
  const variantDisabled = hasVariants && !selectedVariant;

  const lowStockThreshold = 3;
  const stockQuantity = selectedVariant?.stock_reserved != null ? selectedVariant.stock_reserved : (product.stock_reserved ?? 0);
  const isLowStock = stockQuantity > 0 && stockQuantity <= lowStockThreshold;

  // Out of stock: variant selected but has no stock, OR product with no variants has no stock.
  // When no variant is selected yet, don't show out of stock — show the disabled button instead.
  const isOutOfStock = (hasVariants && selectedVariant && stockQuantity <= 0) || (!hasVariants && (product.stock_reserved ?? 0) <= 0);

  return (
    <div className="flex flex-col gap-5">
      {isLowStock && (
        <div className="bg-linear-to-r- from-primary-100/30- to-primary-200/30- border-primary-100- text-secondary-900- px-4 py-3 rounded-lg shadow-lg- border-2- ">
          <div className="flex items-center gap-3">
            <div className="shrink-0">
              <svg className="w-6 h-6 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-bold text-md">{stockQuantity === 1 ? '¡Última unidad disponible!' : `¡Solo quedan ${stockQuantity} unidades!`}</p>
              <p className="text-sm text-secondary-900- mt-1">{stockQuantity === 1 ? 'Aprovechá esta oportunidad única antes de que se agote' : 'Stock limitado, ¡agregá al carrito ahora!'}</p>
            </div>
          </div>
        </div>
      )}
      <ProductVariantSelector product={product as IProduct & { id: string }} onVariantChange={handleVariantChange} />
      <ProductQtySelector product={product as IProduct & { id: string }} initialQty={1} minQty={1} maxQty={10} onQuantityChange={handleQuantityChange} />
      <div>
        <ProductAddToCartButton
          product={product as IProduct & { id: string }}
          selectedVariant={selectedVariant}
          quantity={quantity}
          disabled={variantDisabled}
          outOfStock={isOutOfStock}
          className="mt-6 w-full"
        />
      </div>
    </div>
  );
};

export default ProductSelectors;
